import { useContext, useEffect, useRef, useState } from 'react';
import { QueryKey, useInfiniteQuery } from '@tanstack/react-query';

import { DataPoint } from '../../types';
import { DasWBGT, OffWorkInfo } from '../../types/Device';
import {
  WebSocketCoordinates,
  WebSocketDasWBGTDataPoint,
} from '../../types/Websocket';

import { getProjectDasWBGT } from '../../apis/DasWbgtApi';

import { PubSubContext } from '../../utils/PubSub';

type UseProjectDasCTagMapParams = {
  queryKey: QueryKey;
  projectId: string | null | undefined;
  indexBy?: 'id' | 'dasId';
  orderBy?: 'das_id' | 'last_data_point_received_at';
  queryOptions?: {
    enabled?: boolean;
  };
  onProgress?: (progress) => void;
};

type DasWBGTMap = { [id: string]: DasWBGT | undefined };

export const useProjectDasWBGTMapWithPages = ({
  projectId,
  indexBy = 'id',
  orderBy,
  queryKey,
  queryOptions,
  onProgress,
}: UseProjectDasCTagMapParams) => {
  const dataPointQueueRef = useRef<WebSocketDasWBGTDataPoint[]>([]);
  const coordinatesQueueRef = useRef<WebSocketCoordinates[]>([]);
  const offWorkQueueRef = useRef<OffWorkInfo[]>([]);
  const pubSub = useContext(PubSubContext);
  const [dasWBGTMap, setDasWBGTMap] = useState<DasWBGTMap>({});
  const [total, setTotal] = useState(0);

  const { data, ...others } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const res = await getProjectDasWBGT({
        projectId: projectId as string,
        params: {
          orderBy,
          nextCursor: pageParam,
        },
      });
      setTotal((origin) => {
        if (origin !== res.data.metadata.total) {
          return res.data.metadata.total;
        }

        return origin;
      });
      return res.data;
    },
    getNextPageParam: (res) => res.paging.nextCursor || undefined,
    enabled: !!projectId && (queryOptions?.enabled ?? true),
    refetchOnWindowFocus: false,
    initialPageParam: undefined,
  });

  useEffect(() => {
    if (data) {
      const newDasWBGTMap: DasWBGTMap = {};
      let count = 0;
      data.pages
        .map((page) => page.data)
        .flat()
        .forEach((device) => {
          count++;
          if (indexBy === 'dasId') {
            newDasWBGTMap[device.dasId] = device;
          } else if (indexBy === 'id') {
            newDasWBGTMap[device.id] = device;
          }
        });
      setDasWBGTMap((origin) => ({ ...origin, ...newDasWBGTMap }));
      onProgress?.((count / (total == 0 ? 1 : total)) * 100);
    }
  }, [data, indexBy, total]);

  useEffect(() => {
    const dataPointHandler = (datapointData: WebSocketDasWBGTDataPoint) => {
      if (projectId === datapointData.endpoint.metadata['dsm/projectId']) {
        dataPointQueueRef.current.push(datapointData);
      }
    };

    const coordinatesHandler = (coordinatesData: WebSocketCoordinates) => {
      if (projectId === coordinatesData.endpoint.metadata['dsm/projectId']) {
        coordinatesQueueRef.current.push(coordinatesData);
      }
    };

    pubSub?.subscribe('daswbgt:data-point-received', dataPointHandler);
    pubSub?.subscribe('coordinates-updated', coordinatesHandler);

    return () => {
      pubSub?.unsubscribe('daswbgt:data-point-received', dataPointHandler);
      pubSub?.unsubscribe('coordinates-updated', coordinatesHandler);
    };
  }, [pubSub, projectId, indexBy]);

  useEffect(() => {
    const dataPointQueueTimer = setInterval(() => {
      const dataPoints: WebSocketDasWBGTDataPoint[] = [];

      while (dataPointQueueRef.current.length > 0) {
        const newDataPoint =
          dataPointQueueRef.current.shift() as WebSocketDasWBGTDataPoint;
        dataPoints.push(newDataPoint);
      }

      if (dataPoints.length > 0) {
        setDasWBGTMap((origin) => {
          const newDasCTagMap = { ...origin };
          dataPoints
            .filter((datapointData) => {
              return !!origin[datapointData.endpoint[indexBy]];
            })
            .map((datapointData) => {
              const newDasWBGT = origin[datapointData.endpoint[indexBy]];

              if (newDasWBGT) {
                const updateDataPoint: { [key: string]: DataPoint<any> } =
                  Object.entries(datapointData.dataPoint).reduce(
                    (prev, [key, value]) => {
                      if (value !== undefined || value !== null) {
                        prev[key] = {
                          timestamp: datapointData.timestamp,
                          value,
                          stale: false,
                        };
                      }
                      return prev;
                    },
                    {},
                  );

                newDasWBGT.shadow.dataPoint = {
                  ...newDasWBGT.shadow.dataPoint,
                  ...updateDataPoint,
                };

                newDasCTagMap[newDasWBGT[indexBy]] = newDasWBGT;
              }
            });

          return newDasCTagMap;
        });
      }
    }, 1000);

    const coordinatesQueueTimer = setInterval(() => {
      const coords: WebSocketCoordinates[] = [];

      while (coordinatesQueueRef.current.length > 0) {
        const webSocketCoordinates =
          coordinatesQueueRef.current.shift() as WebSocketCoordinates;
        coords.push(webSocketCoordinates);
      }

      if (coords.length > 0) {
        setDasWBGTMap((origin) => {
          const newDasCTagMap = { ...origin };
          coords
            .filter((coord) => {
              return !!origin[coord.endpoint[indexBy]];
            })
            .map((coord) => {
              const newDasCTag = origin[coord.endpoint[indexBy]];

              if (newDasCTag) {
                newDasCTag.coordinates = coord.coordinates;
                newDasCTag.coordinatesSource = coord.source;
                newDasCTag.lastCoordinatesUpdatedAt = coord.timestamp;
                newDasCTagMap[newDasCTag[indexBy]] = newDasCTag;
              }
            });

          return newDasCTagMap;
        });
      }
    }, 1000);

    const offWorkQueueTimer = setInterval(() => {
      const offWorkInfos: OffWorkInfo[] = [];

      while (offWorkQueueRef.current.length > 0) {
        const offWorkInfo = offWorkQueueRef.current.shift() as OffWorkInfo;
        offWorkInfos.push(offWorkInfo);
      }

      if (offWorkInfos.length > 0) {
        setDasWBGTMap((origin) => {
          const newDasCTagMap = { ...origin };
          offWorkInfos
            .filter((offWork) => {
              return !!origin[offWork.endpoint[indexBy]];
            })
            .map((offWork) => {
              const newDasCTag = origin[offWork.endpoint[indexBy]];

              if (newDasCTag) {
                newDasCTagMap[newDasCTag[indexBy]] = newDasCTag;
              }
            });

          return newDasCTagMap;
        });
      }
    }, 1000);

    return () => {
      clearInterval(dataPointQueueTimer);
      clearInterval(coordinatesQueueTimer);
      clearInterval(offWorkQueueTimer);
    };
  }, []);

  return {
    data: dasWBGTMap,
    ...others,
  };
};
